import { buildGraph, computeRoute as routeCompute } from './router.js';

const ZOOM = 3.0;
const SVG_NS = 'http://www.w3.org/2000/svg';
const XLINK_NS = 'http://www.w3.org/1999/xlink';
const $ = s => document.querySelector(s);
const svg = $('#map');
const floorplanEl = $('#floorplan');
const routeLayer = $('#route-layer');
let imgSize = null;       // natural {w, h} of the floorplan image
let currentRoute = null;  // last route result
let currentStep = -1;     // -1 = overview, 0..n-1 = step index
let currentViewBox = null;// [x, y, w, h]
let animHandle = null;
let floorMetas = [];      // from floors.json
let currentGraph = null;  // built graph for the active floor
let currentPtToM = 0.258; // updated per floor from metadata
const PADDING_PX = 120;   // context padding around the zoomed area (px)

// ---- Turn-by-turn navigation state ----
const NAV_STEP_INTERVAL_MS = 3500;   // auto-advance cadence
let navRotate = true;                // rotate map to walking direction
let navPlay = { active: false, timer: null };
let cursorAnim = null;               // rAF handle for the cursor tween

async function json(url) {
  const r = await fetch(url);
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body.error || r.statusText);
  return body;
}

// Wrapper so callers can swap in caching/offline logic later without touching
// every call site.
const fetchJson = json;

// Pillar label sort key — handles "B1/42", "ST-2/7a" etc. by extracting the
// numeric id so 1, 2, 3, ... 179 stays numeric (not lexicographic 1, 10, 11…).
function pillarSortKey(label) {
  const m = label.match(/^[A-Z0-9\-]+\/(\d+)([a-z]?)$/);
  return m ? parseInt(m[1], 10) + (m[2] ? m[2].charCodeAt(0) / 1000 : 0)
           : Number.POSITIVE_INFINITY;
}

function makeEl(tag, attrs, text) {
  const el = document.createElement(tag);
  if (attrs) for (const k of Object.keys(attrs)) el.setAttribute(k, attrs[k]);
  if (text != null) el.textContent = text;
  return el;
}

function makeSvg(tag, attrs) {
  const el = document.createElementNS(SVG_NS, tag);
  if (attrs) for (const k of Object.keys(attrs)) el.setAttribute(k, attrs[k]);
  return el;
}

function replaceChildren(parent, nodes) {
  while (parent.firstChild) parent.removeChild(parent.firstChild);
  for (const n of nodes) parent.appendChild(n);
}

// ---- Combobox: input + tappable chevron + scrollable picker ----
//
// Mirrors what <datalist> aims to do but reliably renders a real, tappable
// list on iOS Safari (where datalist's native dropdown is cramped and
// inconsistent). Typing filters; the chevron toggles the full list; clicking
// outside closes. The selected value is written to the input and a `change`
// event is dispatched so existing route-recompute logic fires unchanged.
function setupCombo(inputId, panelId, toggleId) {
  const input = document.getElementById(inputId);
  const panel = document.getElementById(panelId);
  const toggle = document.getElementById(toggleId);
  let openedByToggle = false;

  function setExpanded(open) {
    panel.hidden = !open;
    input.setAttribute('aria-expanded', open ? 'true' : 'false');
  }
  function open() { setExpanded(true); filter(input.value); }
  function close() { setExpanded(false); openedByToggle = false; }
  function filter(query) {
    const q = (query || '').trim().toLowerCase();
    let visible = 0;
    for (const opt of panel.querySelectorAll('.combo-option')) {
      const match = !q || opt.dataset.value.toLowerCase().includes(q);
      opt.hidden = !match;
      if (match) visible++;
    }
    let empty = panel.querySelector('.combo-empty');
    if (!visible) {
      if (!empty) {
        empty = makeEl('div', { class: 'combo-empty' }, 'No matches');
        panel.appendChild(empty);
      }
    } else if (empty) {
      empty.remove();
    }
  }

  input.addEventListener('focus', () => open());
  input.addEventListener('input', () => { setExpanded(true); filter(input.value); });
  // Closing via blur is delayed so a tap on an option still registers
  // before the panel hides.
  input.addEventListener('blur', () => {
    setTimeout(() => {
      if (!panel.matches(':hover') && !toggle.matches(':active')) close();
    }, 150);
  });
  toggle.addEventListener('mousedown', e => e.preventDefault()); // don't steal focus
  toggle.addEventListener('click', () => {
    if (panel.hidden) {
      openedByToggle = true;
      open();
      // Show the full list (clear any typed filter) when opened by chevron.
      filter('');
    } else {
      close();
    }
  });
  panel.addEventListener('click', e => {
    const opt = e.target.closest('.combo-option');
    if (!opt) return;
    input.value = opt.dataset.value;
    close();
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
  // Tap-anywhere-else to close.
  document.addEventListener('click', e => {
    if (!input.contains(e.target) && !panel.contains(e.target) && !toggle.contains(e.target)) {
      close();
    }
  });
}

function populateCombo(panelId, items) {
  const panel = document.getElementById(panelId);
  if (!panel) return;
  replaceChildren(panel, items.map(item =>
    makeEl('div', { class: 'combo-option', 'data-value': item.label, role: 'option' }, item.label)
  ));
}

// ---- viewBox helpers (zoom/pan with animation) ----

function setViewBox(box, immediate) {
  if (!imgSize) return;
  // Clamp to image so we never scroll past the canvas edges.
  const [x, y, w, h] = clampViewBox(box);
  if (immediate || !currentViewBox) {
    svg.setAttribute('viewBox', `${x} ${y} ${w} ${h}`);
    currentViewBox = [x, y, w, h];
    return;
  }
  const start = currentViewBox.slice();
  const end = [x, y, w, h];
  const duration = 450;
  const t0 = performance.now();
  if (animHandle) cancelAnimationFrame(animHandle);
  const step = (now) => {
    const t = Math.min(1, (now - t0) / duration);
    // Cubic ease-out for the pleasant "camera settle" feel.
    const e = 1 - Math.pow(1 - t, 3);
    const cur = start.map((v, i) => v + (end[i] - v) * e);
    svg.setAttribute('viewBox', cur.join(' '));
    currentViewBox = cur;
    if (t < 1) animHandle = requestAnimationFrame(step);
    else animHandle = null;
  };
  animHandle = requestAnimationFrame(step);
}

function clampViewBox(box) {
  if (!imgSize) return box;
  let [x, y, w, h] = box;
  // Don't zoom out past the image, don't allow negative origin beyond image.
  w = Math.min(w, imgSize.w);
  h = Math.min(h, imgSize.h);
  x = Math.max(0, Math.min(x, imgSize.w - w));
  y = Math.max(0, Math.min(y, imgSize.h - h));
  return [x, y, w, h];
}

function bboxForPoints(ptsPt, extraPtsPx) {
  // Accept route points in PDF-pt plus optional extra points already in px.
  const pts = [];
  for (const [x, y] of (ptsPt || [])) pts.push([x * ZOOM, y * ZOOM]);
  for (const [x, y] of (extraPtsPx || [])) pts.push([x, y]);
  if (!pts.length) return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const [x, y] of pts) {
    if (x < minX) minX = x; if (y < minY) minY = y;
    if (x > maxX) maxX = x; if (y > maxY) maxY = y;
  }
  return [minX, minY, maxX, maxY];
}

function sheetOverlapPx() {
  // How many CSS pixels of the map-wrap are currently hidden under the
  // bottom sheet. On desktop the sheet is a side panel (overlap=0).
  const sheet = $('#sheet');
  const wrap = $('#map-wrap');
  if (!sheet || !wrap) return 0;
  const ss = window.getComputedStyle(sheet);
  if (ss.position !== 'fixed') return 0;  // desktop / side-panel mode
  const sRect = sheet.getBoundingClientRect();
  const wRect = wrap.getBoundingClientRect();
  return Math.max(0, wRect.bottom - sRect.top);
}

function viewBoxForBBox(bbox, pad, opts) {
  opts = opts || {};
  if (!bbox || !imgSize) return [0, 0, imgSize?.w || 1, imgSize?.h || 1];
  const [minX, minY, maxX, maxY] = bbox;
  pad = pad == null ? PADDING_PX : pad;
  const wrap = $('#map-wrap').getBoundingClientRect();
  const ax = (opts.anchor && opts.anchor.x) ?? 0.5;
  const ay = (opts.anchor && opts.anchor.y) ?? 0.5;
  const aspect = wrap.width / wrap.height;
  let w = (maxX - minX) + 2 * pad;
  let h = (maxY - minY) + 2 * pad;
  if (w / h > aspect) h = w / aspect;
  else w = h * aspect;
  // The bbox defines the zoom LEVEL (how much to include). The anchor point
  // is the FOCAL of the view — the thing we want under the cursor. Default
  // is the bbox centre, but for step-navigation we pass the step's exact
  // (x, y) as `focal` so the cursor lands directly on the step vertex.
  const fx = opts.focal ? opts.focal[0] : (minX + maxX) / 2;
  const fy = opts.focal ? opts.focal[1] : (minY + maxY) / 2;
  return [fx - ax * w, fy - ay * h, w, h];
}

function zoomToOverview() {
  stopAutoPlay();
  setMapRotation(0);
  setCursor(false);
  currentStep = -1;
  highlightStep(-1);
  setViewBox([0, 0, imgSize.w, imgSize.h]);
}

function zoomToRoute() {
  if (!currentRoute) { zoomToOverview(); return; }
  stopAutoPlay();
  setMapRotation(0);
  setCursor(false);
  currentStep = -1;
  highlightStep(-1);
  const bbox = bboxForPoints(currentRoute.points_pt, [
    [currentRoute.from.x_pt * ZOOM, currentRoute.from.y_pt * ZOOM],
    [currentRoute.to.x_pt * ZOOM, currentRoute.to.y_pt * ZOOM],
  ]);
  setViewBox(viewBoxForBBox(bbox));
}

// Step position utilities
function stepXY(stepIdx) {
  const s = currentRoute?.steps?.[stepIdx];
  if (!s) return null;
  return [s.x * ZOOM, s.y * ZOOM];
}

function bearingBetween(fromIdx, toIdx) {
  const a = stepXY(fromIdx), b = stepXY(toIdx);
  if (!a || !b) return 0;
  return Math.atan2(b[1] - a[1], b[0] - a[0]) * 180 / Math.PI;
}

// Compute walking-direction bearing for a given step. Step positions are
// prospective: step[N].position = where the user currently is; step[N].text
// describes the walk ABOUT TO HAPPEN toward waypoint N+1. So the map should
// orient toward the OUTGOING direction — the walk the user is about to
// take. Skips colocated successors ("Arrived" sometimes sits at the same
// position as the final walk step); falls back to incoming bearing only
// for the very last step where there's no walk ahead.
function headingForStep(idx) {
  if (!currentRoute) return 0;
  const steps = currentRoute.steps || [];
  if (idx < 0 || idx >= steps.length) return 0;
  for (let j = idx + 1; j < steps.length; j++) {
    const a = stepXY(idx), b = stepXY(j);
    if (a && b && (a[0] !== b[0] || a[1] !== b[1])) return bearingBetween(idx, j);
  }
  for (let j = idx - 1; j >= 0; j--) {
    const a = stepXY(j), b = stepXY(idx);
    if (a && b && (a[0] !== b[0] || a[1] !== b[1])) return bearingBetween(j, idx);
  }
  return 0;
}

function zoomToStep(stepIdx, opts) {
  opts = opts || {};
  if (!currentRoute) return;
  const steps = currentRoute.steps || [];
  if (stepIdx < 0 || stepIdx >= steps.length) return;
  const previousStep = currentStep;
  currentStep = stepIdx;
  highlightStep(stepIdx);

  // Show cursor + apply rotation (direction of travel).
  setCursor(true);
  const heading = navRotate ? headingForStep(stepIdx) : null;
  const targetRotation = heading == null ? 0 : -(heading + 90);

  // Haptic bump on step change (mobile only; silently ignored elsewhere).
  if (navigator.vibrate && !opts.silent) navigator.vibrate(10);

  // Determine camera framing (zoom level) for this step.
  const focusPx = stepXY(stepIdx);
  const winPts = [focusPx];
  const nextXY = stepXY(stepIdx + 1);
  if (nextXY) winPts.push(nextXY);
  const prevXY = stepXY(stepIdx - 1);
  if (prevXY) winPts.push(prevXY);
  const bbox = bboxForPoints(null, winPts);
  const targetVB = viewBoxForBBox(bbox, 180, {
    anchor: CURSOR_ANCHOR,
    focal: focusPx,
  });

  // When advancing to the NEXT sequential step, animate the camera CONTINUOUSLY
  // along the polyline between the two step vertices. Otherwise (jumping to an
  // arbitrary step, first load, or auto-play start), use the straight tween.
  if (opts.continuous === false || previousStep < 0 || stepIdx !== previousStep + 1) {
    setMapRotation(targetRotation);
    setViewBox(targetVB);
    return;
  }
  travelCameraAlongRoute(previousStep, stepIdx, targetVB, targetRotation);
}

// Slide the camera smoothly along the route polyline from step A to step B.
// The cursor (DOM element) stays pinned at the anchor, so what the user sees
// is the map "scrolling" under them in the direction of travel.
function travelCameraAlongRoute(fromIdx, toIdx, finalVB, finalRotation) {
  const fromXY = stepXY(fromIdx), toXY = stepXY(toIdx);
  if (!fromXY || !toXY) {
    setMapRotation(finalRotation);
    setViewBox(finalVB);
    return;
  }
  // Find the polyline points between the two step vertices.
  const pts = currentRoute.points_pt.map(([x, y]) => [x * ZOOM, y * ZOOM]);
  const findNearest = (xy) => {
    let best = 0, bd = Infinity;
    for (let i = 0; i < pts.length; i++) {
      const dx = pts[i][0] - xy[0], dy = pts[i][1] - xy[1];
      const d = dx * dx + dy * dy;
      if (d < bd) { bd = d; best = i; }
    }
    return best;
  };
  const i0 = findNearest(fromXY), i1 = findNearest(toXY);
  const pathPts = [];
  if (i1 >= i0) for (let i = i0; i <= i1; i++) pathPts.push(pts[i]);
  else for (let i = i0; i >= i1; i--) pathPts.push(pts[i]);
  if (pathPts.length < 2) {
    setMapRotation(finalRotation);
    setViewBox(finalVB);
    return;
  }
  // Cumulative distance along the path.
  const dists = [0];
  for (let i = 1; i < pathPts.length; i++) {
    const dx = pathPts[i][0] - pathPts[i-1][0];
    const dy = pathPts[i][1] - pathPts[i-1][1];
    dists.push(dists[i-1] + Math.hypot(dx, dy));
  }
  const totalDist = dists[dists.length - 1];
  // Per-segment bearings so we can rotate the map as walking direction changes.
  const bearings = [];
  for (let i = 1; i < pathPts.length; i++) {
    const dx = pathPts[i][0] - pathPts[i-1][0];
    const dy = pathPts[i][1] - pathPts[i-1][1];
    bearings.push(Math.atan2(dy, dx) * 180 / Math.PI);
  }
  // Camera-pan duration scales with path length but capped. 2.5 px/ms feels
  // brisk; minimum 400 ms keeps short hops from snapping.
  const duration = Math.max(400, Math.min(1600, totalDist / 2.5));
  const t0 = performance.now();
  if (animHandle) cancelAnimationFrame(animHandle);
  const startVBW = currentViewBox ? currentViewBox[2] : finalVB[2];
  const startVBH = currentViewBox ? currentViewBox[3] : finalVB[3];
  const endVBW = finalVB[2], endVBH = finalVB[3];
  // Figure out segment index + local t for a given progress s ∈ [0, totalDist].
  const pointAlong = (s) => {
    if (s <= 0) return { xy: pathPts[0], segIdx: 0 };
    if (s >= totalDist) return { xy: pathPts[pathPts.length-1], segIdx: bearings.length-1 };
    let i = 1;
    while (i < dists.length && dists[i] < s) i++;
    const segLen = dists[i] - dists[i-1];
    const t = segLen > 0 ? (s - dists[i-1]) / segLen : 0;
    const a = pathPts[i-1], b = pathPts[i];
    return { xy: [a[0] + (b[0]-a[0])*t, a[1] + (b[1]-a[1])*t], segIdx: i-1 };
  };
  // Smooth rotation: unwrap the final target to the closest rotation to current.
  const cur = svg._currentRotation || 0;
  let endRot = finalRotation;
  while (endRot - cur > 180) endRot -= 360;
  while (cur - endRot > 180) endRot += 360;
  const step = (now) => {
    const t = Math.min(1, (now - t0) / duration);
    // Ease in-out cubic: gentle start, brisk middle, gentle stop.
    const e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    const s = e * totalDist;
    const { xy, segIdx } = pointAlong(s);
    // Interpolate zoom (viewBox w/h) linearly over the tween too so we
    // ease from the previous step's zoom to the next step's zoom.
    const w = startVBW + (endVBW - startVBW) * e;
    const h = startVBH + (endVBH - startVBH) * e;
    const vb = [xy[0] - CURSOR_ANCHOR.x * w, xy[1] - CURSOR_ANCHOR.y * h, w, h];
    svg.setAttribute('viewBox', clampViewBox(vb).join(' '));
    currentViewBox = vb;
    // Rotation follows per-segment bearing, smoothly interpolated.
    if (navRotate) {
      const segBearing = bearings[segIdx] ?? 0;
      const segRot = -(segBearing + 90);
      // Blend between startRot and segRot using linear based on raw t, but
      // settle on endRot at t=1 so the final heading matches finalRotation.
      const blend = t < 0.85 ? segRot : segRot + (endRot - segRot) * ((t - 0.85) / 0.15);
      svg.style.transform = `rotate(${blend}deg)`;
      svg._currentRotation = blend;
    } else {
      svg.style.transform = '';
      svg._currentRotation = 0;
    }
    if (t < 1) animHandle = requestAnimationFrame(step);
    else animHandle = null;
  };
  animHandle = requestAnimationFrame(step);
}

// ---- Map rotation ----
function setMapRotation(deg) {
  svg.style.transform = deg ? `rotate(${deg}deg)` : '';
  svg._currentRotation = deg || 0;
}

// ---- Progress cursor ----
// The cursor is a plain DOM element pinned to the wrap at the same (x, y)
// that the #map SVG uses as its transform-origin. It never moves relative
// to the viewport; the map rotates/pans beneath it. viewBox math is
// responsible for placing the step focal point at that anchor.
const CURSOR_ANCHOR = { x: 0.5, y: 0.40 }; // fraction of wrap width/height

function setCursor(visible /*, legacyArgs ignored */) {
  // Backwards-compat: old callers passed (x, y, opts). We only need visibility.
  if (typeof visible !== 'boolean') {
    visible = arguments[0] != null && arguments[1] != null;
  }
  const el = $('#nav-cursor');
  if (!el) return;
  el.classList.toggle('visible', visible);
}

// ---- Auto-advance ----
function startAutoPlay() {
  if (!currentRoute) return;
  const total = currentRoute.steps.length;
  if (total < 2) return;
  if (currentStep < 0) zoomToStep(0, { silent: true });
  navPlay.active = true;
  $('#btn-play').textContent = '⏸';
  $('#btn-play').setAttribute('aria-label', 'Pause navigation');
  scheduleNext();
}
function stopAutoPlay() {
  navPlay.active = false;
  if (navPlay.timer) { clearTimeout(navPlay.timer); navPlay.timer = null; }
  const btn = $('#btn-play');
  if (btn) {
    btn.textContent = '▶';
    btn.setAttribute('aria-label', 'Play navigation');
  }
}
function scheduleNext() {
  if (!navPlay.active) return;
  navPlay.timer = setTimeout(() => {
    const total = currentRoute?.steps?.length || 0;
    if (currentStep >= total - 1) { stopAutoPlay(); return; }
    zoomToStep(currentStep + 1);
    scheduleNext();
  }, NAV_STEP_INTERVAL_MS);
}

function highlightStep(idx) {
  for (const el of document.querySelectorAll('.step')) el.classList.remove('active');
  if (idx >= 0) {
    const el = document.querySelector(`.step[data-idx="${idx}"]`);
    if (el) {
      el.classList.add('active');
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }
  const nav = $('#step-nav');
  const total = currentRoute?.steps?.length || 0;
  if (total) {
    nav.hidden = false;
    $('#step-counter').textContent = idx >= 0 ? `${idx + 1}/${total}` : `–/${total}`;
    $('#btn-prev').disabled = idx <= 0;
    $('#btn-next').disabled = idx >= total - 1;
  } else {
    nav.hidden = true;
  }
  updateTurnBanner(idx);
  updateProgressStrip(idx);
  maybeSpeakStep(idx);
  maybeCelebrateArrival(idx);
}

// ---- Next-turn banner (#3) ----
// Picks an emoji/arrow glyph from the step text so users can recognise the
// turn at a glance without reading the instruction.
function turnGlyph(text) {
  const t = (text || '').toLowerCase();
  if (t.includes('turn sharply left')) return '↩';
  if (t.includes('turn sharply right')) return '↪';
  if (t.includes('turn slightly left')) return '↖';
  if (t.includes('turn slightly right')) return '↗';
  if (t.includes('turn left')) return '⬅';
  if (t.includes('turn right')) return '➡';
  if (t.includes('turn around')) return '↻';
  if (t.includes('arrived')) return '🏁';
  if (t.includes('start')) return '⬆';
  if (t.includes('continue')) return '⬆';
  if (t.includes('on your side')) return '📍';
  return '⬆';
}

function updateTurnBanner(idx) {
  const banner = $('#turn-banner');
  if (!currentRoute || idx < 0) { banner.hidden = true; return; }
  const s = currentRoute.steps[idx];
  if (!s) { banner.hidden = true; return; }
  banner.hidden = false;
  $('#turn-glyph').textContent = turnGlyph(s.text);
  const dist = s.distance_m_approx != null ? `${s.distance_m_approx} m` : '';
  $('#turn-distance').textContent = dist || s.text;
  $('#turn-instruction').textContent = dist ? s.text : '';
}

// ---- Progress strip (#7) ----
function updateProgressStrip(idx) {
  const strip = $('#progress-strip');
  if (!currentRoute || idx < 0) { strip.hidden = true; return; }
  strip.hidden = false;
  const total = currentRoute.steps.length;
  const pct = Math.max(0, Math.min(100, (idx / (total - 1)) * 100));
  $('#progress-fill').style.width = pct + '%';
}

// ---- Voice directions (#2) ----
let lastSpokenStep = -1;
function maybeSpeakStep(idx) {
  if (!window.speechSynthesis) return;
  if (idx < 0 || !currentRoute) return;
  if (idx === lastSpokenStep) return;
  lastSpokenStep = idx;
  const step = currentRoute.steps[idx];
  if (!step) return;
  // Cancel any in-flight utterance so each step lands promptly.
  speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(step.text);
  u.rate = 1.05; u.pitch = 1.0; u.volume = 1.0;
  // Try to prefer an English voice (browser will still fall back).
  const voices = speechSynthesis.getVoices();
  const en = voices.find(v => /^en(-|_|$)/i.test(v.lang));
  if (en) u.voice = en;
  speechSynthesis.speak(u);
}

// ---- Arrival celebration (#5) ----
let arrivalFired = false;
function maybeCelebrateArrival(idx) {
  if (!currentRoute) return;
  const total = currentRoute.steps.length;
  if (idx >= total - 1 && !arrivalFired) {
    arrivalFired = true;
    if (navigator.vibrate) navigator.vibrate([20, 40, 60, 40, 100]);
    // Pulse the destination marker by toggling a class on the SVG wrapper.
    $('#route-layer').classList.add('arrived');
    setTimeout(() => $('#route-layer').classList.remove('arrived'), 3600);
    // Stronger final utterance if voice is enabled.
    if (window.speechSynthesis) {
      speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(`You have arrived at ${currentRoute.to.label}.`);
      u.rate = 1.0; u.pitch = 1.0;
      speechSynthesis.speak(u);
    }
  } else if (idx < total - 1) {
    arrivalFired = false; // reset so re-entering the last step re-celebrates
  }
}

function drawRoute(route) {
  currentRoute = route;
  currentStep = -1;
  lastSpokenStep = -1;
  arrivalFired = false;
  if (window.speechSynthesis) speechSynthesis.cancel();
  const pts = route.points_pt.map(([x, y]) => `${x * ZOOM},${y * ZOOM}`).join(' ');
  const children = [
    // Shadow under the path for legibility on any floorplan color.
    makeSvg('polyline', { points: pts, fill: 'none', stroke: 'rgba(0,0,0,.35)',
      'stroke-width': 22, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }),
    makeSvg('polyline', { points: pts, fill: 'none', stroke: '#ef4444',
      'stroke-width': 16, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }),
    // Active-segment glow (drawn under the red line so it shows around it).
    makeSvg('polyline', { id: 'active-segment', points: '', fill: 'none',
      stroke: '#60a5fa', 'stroke-width': 26, 'stroke-linecap': 'round',
      'stroke-linejoin': 'round', opacity: 0 }),
    makeSvg('circle', { class: 'start-marker', cx: route.from.x_pt * ZOOM,
      cy: route.from.y_pt * ZOOM, r: 28, fill: '#10b981',
      stroke: '#0b0d11', 'stroke-width': 4 }),
    makeSvg('circle', { class: 'end-marker', cx: route.to.x_pt * ZOOM,
      cy: route.to.y_pt * ZOOM, r: 22, fill: '#ef4444',
      stroke: '#0b0d11', 'stroke-width': 4 }),
  ];
  replaceChildren(routeLayer, children);
  zoomToRoute();
}

// Split a step text into plain spans plus highlighted "turn ..." spans.
// Input is only from our own server; still, using textContent keeps us safe.
function stepToNodes(text) {
  const parts = text.split(/(turn [a-z ]+)/g);
  const out = [];
  for (const p of parts) {
    if (!p) continue;
    if (p.startsWith('turn ')) out.push(makeEl('span', { class: 'turn' }, p));
    else out.push(document.createTextNode(p));
  }
  return out;
}

function renderSteps(route) {
  const wrap = makeEl('div');
  route.steps.forEach((s, i) => {
    const card = makeEl('div', { class: 'step', 'data-idx': String(i),
      role: 'button', tabindex: '0' });
    card.appendChild(makeEl('span', { class: 'step-num' }, String(i + 1) + '.'));
    const body = makeEl('div');
    for (const n of stepToNodes(s.text)) body.appendChild(n);
    card.appendChild(body);
    card.addEventListener('click', () => zoomToStep(i));
    card.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); zoomToStep(i); }
    });
    wrap.appendChild(card);
  });
  replaceChildren($('#steps'), [wrap]);
}

function renderSummary(route) {
  const box = $('#summary');
  replaceChildren(box, [
    makeEl('strong', null, `${route.total_length_m_approx} m`),
    document.createTextNode(` - ${route.points_pt.length} pts - ${route.steps.length} steps`),
  ]);
}

function showError(msg) {
  replaceChildren($('#steps'), [makeEl('div', { class: 'error' }, msg)]);
  replaceChildren($('#summary'), []);
  replaceChildren(svg, []);
}

function showEmpty() {
  replaceChildren($('#steps'), [makeEl('div', { class: 'empty-state' },
    'Pick a start lobby and a destination pillar.')]);
}

function currentFloor() {
  return $('#floor').value;
}

// Persisted UI state so users don't re-enter after every reload.
const STORAGE_KEY = 'parkquility.ui.v1';
function loadPrefs() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); }
  catch { return {}; }
}
function savePrefs(partial) {
  const cur = loadPrefs();
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...cur, ...partial }));
}

// URL query params (?floor=s4&to=ST-4/20&from=FIRE+LIFT+LOBBY) override
// stored prefs on initial load. Lets the host app deep-link into a route.
function urlPrefs() {
  const q = new URLSearchParams(window.location.search);
  const out = {};
  for (const key of ['floor', 'from', 'to']) {
    const v = q.get(key);
    if (v != null && v !== '') out[key] = v;
  }
  return out;
}

// Merge: URL params win, then localStorage, then defaults.
function resolveInitialPrefs() {
  return { ...loadPrefs(), ...urlPrefs() };
}

// Per-floor cache of valid from/to labels for input validation.
let floorData = { lobbies: [], pillars: [] };

function validOrBlank(value, labels) {
  if (!value) return null;
  const exact = labels.find(l => l === value);
  if (exact) return exact;
  // Case-insensitive match so users typing 'lift-14' still find 'LIFT-14'
  const ci = labels.find(l => l.toLowerCase() === value.toLowerCase());
  return ci || null;
}

function computeRoute() {
  const floor = currentFloor();
  const rawFrom = $('#from').value, rawTo = $('#to').value;
  if (!floor || !rawFrom || !rawTo) return;
  const from = validOrBlank(rawFrom, floorData.lobbies.map(l => l.label));
  const to = validOrBlank(rawTo, floorData.pillars.map(p => p.label));
  if (!from || !to) return; // wait for valid selections
  if (!currentGraph) { showError('Graph not loaded for this floor.'); return; }
  savePrefs({ floor, from, to });
  try {
    const route = routeCompute(currentGraph, from, to, currentPtToM);
    drawRoute(route);
    renderSteps(route);
    renderSummary(route);
  } catch (e) {
    showError(e.message);
  }
}

async function loadFloor(floorId, restoreSelection) {
  const floorMeta = floorMetas.find(f => f.id === floorId);
  if (!floorMeta) throw new Error(`Unknown floor: ${floorId}`);
  const routingOn = !!floorMeta.routing_available;
  currentPtToM = floorMeta.pt_to_m || 0.258;

  // Load floorplan into the SVG's <image>. Use a plain Image() probe to
  // learn natural dimensions before setting the viewBox.
  const imgUrl = floorMeta.floorplan_url;
  const probe = new Image();
  probe.src = imgUrl;
  await new Promise((resolve, reject) => { probe.onload = resolve; probe.onerror = reject; });
  imgSize = { w: probe.naturalWidth, h: probe.naturalHeight };
  floorplanEl.setAttribute('href', imgUrl);
  floorplanEl.setAttributeNS(XLINK_NS, 'xlink:href', imgUrl); // Safari fallback
  floorplanEl.setAttribute('width', imgSize.w);
  floorplanEl.setAttribute('height', imgSize.h);
  setViewBox([0, 0, imgSize.w, imgSize.h], true);
  currentRoute = null; currentStep = -1; currentGraph = null;
  replaceChildren(routeLayer, []);
  replaceChildren($('#summary'), []);
  $('#step-nav').hidden = true;

  // Fetch the graph (only for routing-enabled floors) and build lobby/pillar
  // catalogues directly from its nodes. For map-only floors, fall back to
  // pillars_final.json + pois.json.
  let lobbies = [], pillars = [];
  if (routingOn && floorMeta.graph_url) {
    const graphJson = await fetchJson(floorMeta.graph_url);
    currentGraph = buildGraph(graphJson);
    for (const n of graphJson.nodes) {
      if (n.type === 'lobby' && n.label) {
        lobbies.push({ label: n.label, x_pt: n.x_pt, y_pt: n.y_pt });
      } else if (n.type === 'pillar' && n.label) {
        pillars.push({ label: n.label, x_pt: n.x_pt, y_pt: n.y_pt });
      }
    }
    lobbies.sort((a, b) => a.label.localeCompare(b.label));
    pillars.sort((a, b) => pillarSortKey(a.label) - pillarSortKey(b.label));
  } else {
    // Map-only floor: derive lobbies from pois.json, pillars from pillars_final.json.
    const [pois, pillarData] = await Promise.all([
      fetchJson(floorMeta.pois_url).catch(() => []),
      fetchJson(floorMeta.pillars_url).catch(() => []),
    ]);
    const lobbyTypes = new Set([
      'fire_lift_lobby', 'shuttle_lift_lobby', 'generic_lobby',
      'fire_lift', 'lift', 'shuttle_lift', 'fire_staircase',
    ]);
    lobbies = pois.filter(p => lobbyTypes.has(p.type))
                  .map(p => ({ label: p.text, x_pt: p.x, y_pt: p.y }))
                  .sort((a, b) => a.label.localeCompare(b.label));
    pillars = pillarData.map(p => ({ label: p.id, x_pt: p.x, y_pt: p.y }))
                        .sort((a, b) => pillarSortKey(a.label) - pillarSortKey(b.label));
  }
  floorData = { lobbies, pillars };

  populateCombo('from-panel', lobbies);
  populateCombo('to-panel', pillars);
  $('#from').disabled = !routingOn || lobbies.length === 0;
  $('#to').disabled = !routingOn;

  // Restore previous selections (URL params on first load, else localStorage).
  // URL params intentionally don't need to match a previously-selected floor;
  // if the caller says `?floor=s4&to=ST-4/20`, we honour it directly.
  const prefs = restoreSelection ? resolveInitialPrefs() : {};
  const prefsMatchFloor = prefs.floor === floorId;
  const fromCandidate = prefsMatchFloor ? prefs.from : '';
  const toCandidate = prefsMatchFloor ? prefs.to : '';
  $('#from').value = validOrBlank(fromCandidate, lobbies.map(l => l.label)) || '';
  $('#to').value = validOrBlank(toCandidate, pillars.map(p => p.label)) || '';

  if (!routingOn) {
    replaceChildren($('#steps'), [
      makeEl('div', { class: 'empty-state' },
        `Routing isn't available for ${floorMeta.label} yet — showing the ` +
        `floorplan and destinations only.`),
    ]);
    return;
  }
  if ($('#from').value && $('#to').value) {
    await computeRoute();
  } else if ($('#to').value) {
    // Deep-link with only a destination pillar: highlight + zoom to it so
    // the user sees where their car is while they pick a From.
    zoomToDestination($('#to').value);
    showEmpty();
  } else {
    showEmpty();
  }
}

function zoomToDestination(pillarLabel) {
  const p = floorData.pillars.find(pp => pp.label === pillarLabel);
  if (!p) return;
  const cx = p.x_pt * ZOOM, cy = p.y_pt * ZOOM;
  // Render a standalone destination marker (no route) so the user sees the pin.
  const children = [
    makeSvg('circle', { cx, cy, r: 34, fill: 'rgba(239,68,68,.25)' }),
    makeSvg('circle', { cx, cy, r: 14, fill: '#ef4444',
      stroke: '#0b0d11', 'stroke-width': 4 }),
  ];
  replaceChildren(routeLayer, children);
  // Zoom to a ~240px window centred on the pillar.
  const bbox = [cx - 120, cy - 120, cx + 120, cy + 120];
  setViewBox(viewBoxForBBox(bbox, 60));
}

// Bottom-sheet toggle — expands the directions panel on mobile.
function toggleSheet(force) {
  const sheet = $('#sheet');
  const isOpen = sheet.classList.contains('open');
  const wantOpen = force === undefined ? !isOpen : force;
  sheet.classList.toggle('open', wantOpen);
  // Re-fit the zoom once the sheet finishes sliding so the focus doesn't
  // stay buried under the sheet (or leave too much empty space above when
  // the sheet collapses).
  setTimeout(() => {
    if (currentStep >= 0) zoomToStep(currentStep);
    else if (currentRoute) zoomToRoute();
  }, 280);
}

// ---- Compass heading (#6) ----
// Uses DeviceOrientationEvent.alpha to rotate the map to match the phone's
// actual heading. Independent of route navigation — useful when the user
// is just inspecting the map and wants it to match which way they're facing.
let compassActive = false;
let compassHandler = null;

async function toggleCompass() {
  if (compassActive) {
    if (compassHandler) {
      window.removeEventListener('deviceorientationabsolute', compassHandler);
      window.removeEventListener('deviceorientation', compassHandler);
      compassHandler = null;
    }
    compassActive = false;
    $('#btn-compass').setAttribute('aria-pressed', 'false');
    setMapRotation(0);
    return;
  }
  // iOS Safari requires an explicit permission request from a user gesture.
  if (typeof DeviceOrientationEvent !== 'undefined' &&
      typeof DeviceOrientationEvent.requestPermission === 'function') {
    try {
      const res = await DeviceOrientationEvent.requestPermission();
      if (res !== 'granted') return;
    } catch { return; }
  }
  compassHandler = ev => {
    // `webkitCompassHeading` on iOS is a true compass heading (0 = north, CW).
    // `alpha` is CCW from east on some browsers; we fall back to it if needed.
    const heading = (typeof ev.webkitCompassHeading === 'number')
      ? ev.webkitCompassHeading
      : (ev.alpha != null ? (360 - ev.alpha) % 360 : null);
    if (heading == null) return;
    // Rotate the map so north is "up" minus the phone heading — i.e. what
    // the user is facing points up the screen.
    setMapRotation(-heading);
  };
  // Prefer 'deviceorientationabsolute' when available (true north).
  window.addEventListener('deviceorientationabsolute', compassHandler, true);
  window.addEventListener('deviceorientation', compassHandler, true);
  compassActive = true;
  $('#btn-compass').setAttribute('aria-pressed', 'true');
}

// ---- Pinch-zoom + pan (#8) ----
// Let the user drag the map around and pinch to zoom. Uses pointer events
// so it works for touch, mouse, and trackpad. Any user interaction pauses
// auto-play; tapping ◎ (fit route) or ⤢ (overview) snaps back.
function attachPinchPan(wrap) {
  const pointers = new Map(); // id → {x, y}
  let lastPinchDist = null;
  let lastPanX = null, lastPanY = null;

  const clientToViewBox = (cx, cy) => {
    if (!currentViewBox) return [0, 0];
    const rect = wrap.getBoundingClientRect();
    const [vx, vy, vw, vh] = currentViewBox;
    return [vx + ((cx - rect.left) / rect.width) * vw,
            vy + ((cy - rect.top) / rect.height) * vh];
  };

  wrap.addEventListener('pointerdown', e => {
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    wrap.setPointerCapture(e.pointerId);
    if (pointers.size === 1) { lastPanX = e.clientX; lastPanY = e.clientY; }
    if (pointers.size === 2) {
      const [a, b] = [...pointers.values()];
      lastPinchDist = Math.hypot(a.x - b.x, a.y - b.y);
    }
    stopAutoPlay();
  });

  wrap.addEventListener('pointermove', e => {
    if (!pointers.has(e.pointerId) || !currentViewBox) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.size === 1) {
      const dx = e.clientX - lastPanX, dy = e.clientY - lastPanY;
      lastPanX = e.clientX; lastPanY = e.clientY;
      const rect = wrap.getBoundingClientRect();
      const vbDx = -dx * currentViewBox[2] / rect.width;
      const vbDy = -dy * currentViewBox[3] / rect.height;
      const vb = [currentViewBox[0] + vbDx, currentViewBox[1] + vbDy,
                  currentViewBox[2], currentViewBox[3]];
      svg.setAttribute('viewBox', clampViewBox(vb).join(' '));
      currentViewBox = vb;
    } else if (pointers.size === 2) {
      const [a, b] = [...pointers.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      if (lastPinchDist) {
        const factor = lastPinchDist / dist;   // >1 = zoom out
        const cx = (a.x + b.x) / 2, cy = (a.y + b.y) / 2;
        const [vbCx, vbCy] = clientToViewBox(cx, cy);
        const [vx, vy, vw, vh] = currentViewBox;
        const newW = Math.max(100, Math.min(imgSize.w, vw * factor));
        const newH = Math.max(100, Math.min(imgSize.h, vh * factor));
        const newVx = vbCx - ((vbCx - vx) * (newW / vw));
        const newVy = vbCy - ((vbCy - vy) * (newH / vh));
        const vb = [newVx, newVy, newW, newH];
        svg.setAttribute('viewBox', clampViewBox(vb).join(' '));
        currentViewBox = vb;
      }
      lastPinchDist = dist;
    }
  });

  const endPointer = e => {
    pointers.delete(e.pointerId);
    if (pointers.size < 2) lastPinchDist = null;
  };
  wrap.addEventListener('pointerup', endPointer);
  wrap.addEventListener('pointercancel', endPointer);

  // Wheel zoom on desktop/trackpad — Ctrl+scroll or trackpad pinch.
  wrap.addEventListener('wheel', e => {
    if (!currentViewBox) return;
    e.preventDefault();
    const factor = Math.pow(1.0015, e.deltaY); // small, smooth
    const [vbCx, vbCy] = clientToViewBox(e.clientX, e.clientY);
    const [vx, vy, vw, vh] = currentViewBox;
    const newW = Math.max(100, Math.min(imgSize.w, vw * factor));
    const newH = Math.max(100, Math.min(imgSize.h, vh * factor));
    const newVx = vbCx - ((vbCx - vx) * (newW / vw));
    const newVy = vbCy - ((vbCy - vy) * (newH / vh));
    const vb = [newVx, newVy, newW, newH];
    svg.setAttribute('viewBox', clampViewBox(vb).join(' '));
    currentViewBox = vb;
  }, { passive: false });
}

// ---- Georef scaffolding ----
// If a floor config includes {nw, ne, sw, se} corner lat/long pairs AND their
// corresponding x_px coordinates on the floorplan, we can project a GPS
// reading into floorplan px using a 2D affine transform. Disabled by default
// — the real product code needs to decide if/when to enable.
const GEOREF = {}; // keyed by floor id; populated by /floors if server ships it

function gpsToFloorplanPx(floorId, lat, lng) {
  const g = GEOREF[floorId];
  if (!g) return null;
  // Affine from (lat, lng) to (x_px, y_px). Given two diagonal corners we
  // can fit a simple non-rotating scale+translate. For robustness, use all
  // 4 corners to solve a 6-parameter affine via least squares.
  // (Left as a stub — wiring in when real corner data lands.)
  return null;
}

async function init() {
  showEmpty();
  // Re-fit on resize: keep the current viewBox (SVG handles aspect) but
  // refresh the route framing so landscape/portrait rotations stay sensible.
  window.addEventListener('resize', debounce(() => {
    if (currentStep >= 0) zoomToStep(currentStep, { continuous: false });
    else if (currentRoute) zoomToRoute();
  }, 200));

  floorMetas = await fetchJson('/floors.json');
  if (!floorMetas.length) { showError('No floors available.'); return; }
  const floorSel = $('#floor');
  for (const f of floorMetas) {
    floorSel.appendChild(makeEl('option', { value: f.id }, f.label));
  }

  const prefs = resolveInitialPrefs();
  const initialFloor = floorMetas.find(f => f.id === prefs.floor) ? prefs.floor : floorMetas[0].id;
  floorSel.value = initialFloor;

  floorSel.addEventListener('change', () => {
    savePrefs({ floor: currentFloor() });
    loadFloor(currentFloor(), false).catch(e => showError(e.message));
  });
  const fromEl = $('#from'), toEl = $('#to');
  // Fire on both 'input' (typing) and 'change' (combo pick or paste).
  const debounced = debounce(computeRoute, 120);
  fromEl.addEventListener('change', computeRoute);
  toEl.addEventListener('change', computeRoute);
  fromEl.addEventListener('input', debounced);
  toEl.addEventListener('input', debounced);

  // Wire the custom combobox dropdowns (replaces the iOS-flaky <datalist>).
  setupCombo('from', 'from-panel', 'from-toggle');
  setupCombo('to', 'to-panel', 'to-toggle');

  // Sheet open/close on mobile.
  const handle = $('#sheet-handle');
  handle.addEventListener('click', () => toggleSheet());
  handle.addEventListener('keydown', e => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSheet(); }
  });

  // Zoom controls
  $('#btn-zoom-overview').addEventListener('click', zoomToOverview);
  $('#btn-zoom-route').addEventListener('click', zoomToRoute);

  // Step prev/next — any manual interaction pauses auto-play.
  $('#btn-prev').addEventListener('click', () => {
    stopAutoPlay();
    if (currentStep <= 0) zoomToStep(0);
    else zoomToStep(currentStep - 1);
  });
  $('#btn-next').addEventListener('click', () => {
    stopAutoPlay();
    const total = currentRoute?.steps?.length || 0;
    if (currentStep < 0) zoomToStep(0);
    else if (currentStep < total - 1) zoomToStep(currentStep + 1);
  });

  // Auto-play toggle
  $('#btn-play').addEventListener('click', () => {
    if (navPlay.active) stopAutoPlay();
    else startAutoPlay();
  });

  // Rotate-to-heading toggle
  $('#btn-rotate').addEventListener('click', () => {
    navRotate = !navRotate;
    $('#btn-rotate').setAttribute('aria-pressed', navRotate ? 'true' : 'false');
    if (currentStep >= 0) {
      zoomToStep(currentStep, { silent: true, continuous: false });
    } else {
      setMapRotation(0);
    }
  });

  // Swap From/To (#4)
  $('#btn-swap').addEventListener('click', () => {
    const f = $('#from').value, t = $('#to').value;
    $('#from').value = t; $('#to').value = f;
    computeRoute();
  });

  // Compass / phone heading toggle (#6)
  $('#btn-compass').addEventListener('click', toggleCompass);

  // Pinch-zoom + pan (#8)
  attachPinchPan($('#map-wrap'));

  await loadFloor(initialFloor, true);
  // After a successful route, peek the sheet open once so the user sees
  // directions are available; only on mobile where the sheet is hidden.
  if (window.matchMedia('(max-width: 767px)').matches && $('#steps').children.length) {
    setTimeout(() => toggleSheet(true), 400);
  }
}

function debounce(fn, ms) {
  let t = null;
  return (...args) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

init().catch(e => showError(e.message));
